import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Detailed loan product page for a broker/lead-gen site.
 * All rates, APRs, and terms are placeholders — actual terms are set by the lending partner.
 */
export default function LoanProductPage({
  slug,
  overline,
  title,
  italic,
  subtitle,
  hero,
  minAmount,
  maxAmount,
  aprRange,
  termRange,
  monthlyExample,
  eligibility = [],
  benefits = [],
  faqs = [],
}) {
  return (
    <div className="text-white">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-20 ef-hero-radial ef-noise">
        {hero?.image && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.10]"
            style={{ backgroundImage: `url('${hero.image}')` }}
          />
        )}
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="ef-overline">{overline}</div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 tracking-tight leading-[1.02]"
            >
              {title} <em className="ef-gold-text not-italic">{italic}</em>
            </motion.h1>
            <p className="mt-6 text-white/70 max-w-xl text-lg leading-relaxed">{subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/apply?product=${slug}`}>
                <Button data-testid={`${slug}-apply`} className="ef-btn-primary rounded-full px-6 h-12">
                  Apply Now <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:+17865989004">
                <Button data-testid={`${slug}-call`} variant="outline" className="ef-btn-ghost rounded-full px-6 h-12 bg-transparent text-white">Talk to a specialist</Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="ef-card p-7">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">At a Glance</div>
              <div className="mt-4 divide-y divide-white/10">
                <Row label="Min Loan Amount" value={minAmount} />
                <Row label="Max Loan Amount" value={maxAmount} />
                <Row label="APR Range" value={aprRange} />
                <Row label="Term" value={termRange} />
                <Row label="Example Monthly Payment" value={monthlyExample} />
              </div>
              <p className="mt-5 text-[10px] text-white/40 leading-relaxed">
                Illustrative only. Actual amounts, APR, and terms are determined by the lending partner based on credit and other factors. Not a credit offer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility + Benefits */}
      <section className="py-20 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid md:grid-cols-2 gap-8">
          <div className="ef-card p-8" data-testid={`${slug}-eligibility`}>
            <div className="ef-overline">Eligibility Requirements</div>
            <h3 className="font-display text-2xl font-bold mt-3">Who can apply</h3>
            <ul className="mt-6 space-y-3">
              {eligibility.map((e) => (
                <li key={e} className="flex items-start gap-2.5 text-sm text-white/80">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#17A673] shrink-0" /> {e}
                </li>
              ))}
            </ul>
          </div>
          <div className="ef-card p-8" data-testid={`${slug}-benefits`}>
            <div className="ef-overline">Benefits</div>
            <h3 className="font-display text-2xl font-bold mt-3">What you get</h3>
            <ul className="mt-6 space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-white/80">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#D4AF37] shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-20">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <div className="ef-overline flex items-center gap-2"><HelpCircle className="h-3.5 w-3.5" /> FAQ</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold mt-3">Common questions</h3>
            <Accordion type="single" collapsible className="mt-8" data-testid={`${slug}-faq`}>
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`i-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left font-display text-lg hover:no-underline hover:text-[#D4AF37]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 text-sm leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="ef-card p-10 text-center bg-gradient-to-br from-[#1B63F0]/10 to-[#17A673]/10">
            <div className="ef-overline">Ready when you are</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold mt-3">Apply in about 5 minutes.</h3>
            <p className="mt-3 text-white/60 max-w-lg mx-auto">No SSN or bank credentials collected on our site. We route qualified requests to licensed partner lenders for underwriting.</p>
            <Link to={`/apply?product=${slug}`}>
              <Button data-testid={`${slug}-apply-bottom`} className="ef-btn-primary rounded-full px-7 h-12 mt-7">
                Apply Now <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-3 flex items-baseline justify-between gap-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{label}</div>
      <div className="text-sm font-semibold text-white text-right">{value}</div>
    </div>
  );
}
